import {
  appendAuthTokenToSurveyFileUrl,
  buildImgixUrl,
  buildPrivateImgixUrl,
  isPrivateBucketFileDownloadUrl
} from "@/lib";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";
import {
  getPrivateFileUrlsFromRecord,
  shouldUsePresignedForPrivateDisplay,
  type PrivateFileRecord,
  type PrivateFileTool
} from "@/helpers/private-file-url";
import { useAppSelector } from "@/redux/store/hooks";
import {
  useIsPrivateIdeaMediaBucketEnabled,
  useIsPrivatePlacesMediaBucketEnabled,
  useIsPrivateSurveyMediaBucketEnabled
} from "@/hooks/toggle";

export type {
  PrivateFileRecord,
  PrivateFileTool
} from "@/helpers/private-file-url";

export type PrivateFileDisplayHints = {
  contentType?: string;
  fileName?: string;
  assumeImage?: boolean;
};

export type GetPrivateFileDisplayUrlOptions = {
  fileUrl: string | undefined;
  user: any;
  anonymousUser: any;
  presignedFileUrl?: string;
  isPrivateBucketEnabled?: boolean;
  display?: PrivateFileDisplayHints;
};

export type BuildPrivateMediaImageUrlOptions = {
  imageUrl: string | undefined;
  user: any;
  anonymousUser: any;
  height?: number | "auto";
  width?: number | "auto";
  fit?: string;
  presignedFileUrl?: string;
  isPrivateBucketEnabled?: boolean;
  display?: PrivateFileDisplayHints;
};

export type BuildPrivateFileRecordImageUrlOptions = {
  record: PrivateFileRecord;
  tool: PrivateFileTool;
  user: any;
  anonymousUser: any;
  height?: number | "auto";
  width?: number | "auto";
  fit?: string;
  isPrivateBucketEnabled?: boolean;
  display?: PrivateFileDisplayHints;
};

export type UsePrivateFileRecordImageUrlOptions = {
  record: PrivateFileRecord;
  tool: PrivateFileTool;
  height?: number | "auto";
  width?: number | "auto";
  fit?: string;
  display?: PrivateFileDisplayHints;
};

export type UsePrivateMediaImageUrlOptions = {
  imageUrl: string | undefined;
  height?: number | "auto";
  width?: number | "auto";
  fit?: string;
  presignedFileUrl?: string;
  tool?: PrivateFileTool;
  display?: PrivateFileDisplayHints;
};

export type UsePrivateFileImageUrlOptions = {
  fileUrl: string | undefined;
  presignedFileUrl?: string;
  height?: number | "auto";
  width?: number | "auto";
  fit?: string;
  tool?: PrivateFileTool;
  display?: PrivateFileDisplayHints;
};

function useIsPrivateBucketEnabledForTool(tool: PrivateFileTool): boolean {
  const isSurveyPrivate = useIsPrivateSurveyMediaBucketEnabled();
  const isPlacesPrivate = useIsPrivatePlacesMediaBucketEnabled();
  const isIdeasPrivate = useIsPrivateIdeaMediaBucketEnabled();

  if (tool === "ideas") return isIdeasPrivate;
  if (tool === "places") return isPlacesPrivate;
  return isSurveyPrivate;
}

function getAssumeImageForTool(tool: PrivateFileTool): boolean {
  return tool === "ideas" || tool === "places";
}

export function getPrivateFileDisplayUrl({
  fileUrl,
  user,
  anonymousUser,
  presignedFileUrl,
  isPrivateBucketEnabled = false,
  display
}: GetPrivateFileDisplayUrlOptions): string | undefined {
  if (
    isPrivateBucketEnabled &&
    shouldUsePresignedForPrivateDisplay(presignedFileUrl, display)
  ) {
    return presignedFileUrl;
  }

  const { token } = fetchAuthToken({ user, anonymousUser });
  const isAnonymousParticipant = !user?.registeredUser?.isLoggedIn;
  if (!fileUrl || !isAnonymousParticipant) return fileUrl;
  return appendAuthTokenToSurveyFileUrl(fileUrl, token) ?? fileUrl;
}

export function buildPrivateMediaImageUrl({
  imageUrl,
  user,
  anonymousUser,
  height = 1500,
  width = 1500,
  fit = "clip",
  presignedFileUrl,
  isPrivateBucketEnabled = false,
  display
}: BuildPrivateMediaImageUrlOptions): string | undefined {
  if (
    isPrivateBucketFileDownloadUrl(imageUrl) &&
    shouldUsePresignedForPrivateDisplay(presignedFileUrl, display)
  ) {
    if (presignedFileUrl!.startsWith("data:")) return presignedFileUrl;
    const privateImgixUrl = buildPrivateImgixUrl(
      presignedFileUrl!,
      height,
      width,
      fit
    );
    if (privateImgixUrl?.includes("imgix.net")) return privateImgixUrl;
    return presignedFileUrl;
  }

  const displayUrl = getPrivateFileDisplayUrl({
    fileUrl: imageUrl,
    user,
    anonymousUser,
    presignedFileUrl,
    isPrivateBucketEnabled: false,
    display
  });
  if (!displayUrl || displayUrl.startsWith("data:")) return displayUrl;
  if (displayUrl.includes("imgix.net")) return displayUrl;
  if (isPrivateBucketFileDownloadUrl(displayUrl)) return displayUrl;
  return buildImgixUrl(displayUrl, height, width, fit);
}

export function buildPrivateFileRecordImageUrl({
  record,
  tool,
  user,
  anonymousUser,
  height = 1500,
  width = 1500,
  fit = "clip",
  isPrivateBucketEnabled = false,
  display
}: BuildPrivateFileRecordImageUrlOptions): string | undefined {
  const { downloadUrl, presignedFileUrl } = getPrivateFileUrlsFromRecord(
    record,
    tool
  );

  return buildPrivateMediaImageUrl({
    imageUrl: downloadUrl,
    user,
    anonymousUser,
    height,
    width,
    fit,
    presignedFileUrl,
    isPrivateBucketEnabled,
    display: {
      assumeImage: getAssumeImageForTool(tool),
      ...display
    }
  });
}

export function usePrivateFileRecordImageUrl({
  record,
  tool,
  height = 1500,
  width = 1500,
  fit = "clip",
  display
}: UsePrivateFileRecordImageUrlOptions): string | undefined {
  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const isPrivateBucketEnabled = useIsPrivateBucketEnabledForTool(tool);

  return buildPrivateFileRecordImageUrl({
    record,
    tool,
    user,
    anonymousUser,
    height,
    width,
    fit,
    isPrivateBucketEnabled,
    display
  });
}

export function usePrivateMediaImageUrl({
  imageUrl,
  height = 1500,
  width = 1500,
  fit = "clip",
  presignedFileUrl,
  tool = "ideas",
  display
}: UsePrivateMediaImageUrlOptions): string | undefined {
  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const isPrivateBucketEnabled = useIsPrivateBucketEnabledForTool(tool);

  return buildPrivateMediaImageUrl({
    imageUrl,
    user,
    anonymousUser,
    height,
    width,
    fit,
    presignedFileUrl,
    isPrivateBucketEnabled,
    display: {
      assumeImage: getAssumeImageForTool(tool),
      ...display
    }
  });
}

export function usePrivateFileImageUrl({
  fileUrl,
  presignedFileUrl,
  height = 1500,
  width = 1500,
  fit = "clip",
  tool = "survey",
  display
}: UsePrivateFileImageUrlOptions): string | undefined {
  return usePrivateMediaImageUrl({
    imageUrl: fileUrl,
    height,
    width,
    fit,
    presignedFileUrl,
    tool,
    display
  });
}
